import WishlistHelper from "../../utils/wishlist-helper";
import AnalyticsHelper from "../../utils/analytics-helper";
import CarSaveForLater from '../helpers/car-save-for-later';

export default class LinkParams {
    constructor(parent) {
        this.parent = parent;
        this.data = parent.data;
        this.car = parent.car;
        this.pricing = parent.pricing;
        this.finance = parent.finance;
        this.partEx = parent.partEx;
        this.ecommNudge = parent.ecommNudge;
        this.links = [...parent.el.querySelectorAll('.link-params')];
        this.carSaveForLater = new CarSaveForLater(this);
        this.init();
        this.bind();
    }

    init() {
        this.links.forEach((link) => link.original = link.href);
    }

    modelIdFromLink(link) {
        const href = link.getAttribute('href') || link.href || '';
        return new URLSearchParams(href.split('?')[1] || '').get('model');
    }

    bind() {
        this.links.forEach((link) => link.addEventListener('click', async (e) => {
            e.preventDefault();

            await this.addWishlistParams(link);
            await this.saveForLater(link);
            this.removeLeftoverParams(link);
            this.ecommNudge.sendNudge(link);

            AnalyticsHelper.push(link, link.href);
        }));
    }

    async addWishlistParams(link) {
        if(link.original?.includes('wishlistId') || link.original?.includes('shareCode')) {
            this.parent.summary.showLoader();
            const data = await WishlistHelper.create();
            if(data) {
                link.href = this.replace(link.href, data);
            }
            this.parent.summary.hideLoader();
        }
    }

    async saveForLater(link) {
        // List of buttons that require asynchronous save - they do not redirect to a page that saves the car for later
        const cssClassOfButtonWithAsynchronousSave = ['continue-dealer-button', 'out-of-stock-button', 'order-button'];
        if (cssClassOfButtonWithAsynchronousSave.some((clsName) => link.classList.contains(clsName))) {
            this.parent.summary.showLoader();
            // HMEPM-2077: the button href already carries the canonical Honda Data model ID
            // (e.g. "model=hr-v-hybrid") - pass it so the saved configuration does not store
            // the internal `modelBodyCode` and Salesforce receives the correct model code.
            const data = await this.carSaveForLater.save(this.modelIdFromLink(link));

            if(data) {
                link.href = this.replace(link.href, {
                    id: data.id,
                });
            }
            this.parent.summary.hideLoader();
        }

        // List of buttons that require asynchronous save - they do not redirect to a page that saves the car for later
        const cssClassOfButtonWithRedirectSave = ['save-config--nudge'];
        if (cssClassOfButtonWithRedirectSave.some((clsName) => link.classList.contains(clsName))) {
            const carData = this.car.data();
            const analyticsData = AnalyticsHelper.el(link);
            const data = {
                ...this.carParams(),
                ...this.financeParams(),
            };

            // Clear the previous ecommerce object and then push ecommerce data
            HONDA?.analytics?.push({ ecommerce: null });
            HONDA?.analytics?.push({
                event: "add_to_wishlist",
                ecommerce: {
                    currency: HONDA?.DataLayerHelper?.getCurrencyISO(HONDA?.currency?.symbol),
                    value: data['car-total-price'],
                    items: [
                        {
                            item_id: data['mtoce'],
                            item_name: analyticsData['modelSelected'],
                            affiliation: HONDA?.CarConfigurator?.locale,
                            item_brand: "honda",
                            item_category: analyticsData['businessUnit'],
                            item_category2: analyticsData['modelSelected'],
                            item_list_id: "configurator",
                            item_list_name: "configurator",
                            item_variant: data['mtoce'],
                            price: data['car-total-price'],
                            quantity: 1,
                            business_unit: analyticsData['businessUnit'],
                            colour: data['car-color-code'] + " | " + data['car-color'],
                            accessories: data['car-trim-code'] + " | " + data['car-trim'],
                            accessories2: carData['engineTDCode'] + " | " + carData['engineName'],
                            accessories3: carData['gradeCode'] + " | " + carData['gradeName'],
                            accessories4: data['selected-accessories'],
                            finance: data['finance-offer-type'] === 'cash' ? false : true,
                            finance_option: data['finance-offer-type'],
                            finance_deposit: data['finance-honda-deposit'],
                            finance_term: data['finance-offer-term'],
                            part_exchange: this.partEx.enabled,
                        }
                    ]
                }
            });
        }
    }

    update() {
        const data = {
            ...this.carParams(),
            ...this.specParams(),
            ...this.financeParams(),
            ...this.partExParams(),
        };
        this.links.forEach((link) => link.href = this.replace(link.original, data));
    }

    carParams() {
        const carData = this.car.data();
        const colourTrim = this.car.colourTrim();
        const wheelData = this.car.wheelData();
        const accessoriesData = this.car.accessoriesData();
        const accessoriesWithoutWheelData = this.car.accessoriesWithoutWheelData();
        return {
            'grade-engine': `${carData.gradeName}|${carData.engineName} ${carData.transmissionName} ${carData.drivetrain}`,
            'grade-engine-code': `${carData.gradeCode}|${carData.engineTDCode}`,
            'car-color-code': colourTrim.colour,
            'car-color': colourTrim.colourName,
            'car-trim-code': colourTrim.trim,
            'car-trim': colourTrim.trimName,
            'car-wheel-code': wheelData.partNumber,
            'car-wheel': wheelData.description,
            'selected-accessories': accessoriesData.map((a) => a.description).join(),
            'accessories-without-standard-alloys': accessoriesWithoutWheelData.map((a) => a.description).join(', '),
            'car-base-price': this.pricing.getCarBasePrice(),
            'car-total-price': this.pricing.getCarTotalPrice(),
            'car-long-url': window.location.href,
            'mtoce': this.car.mtoce(),
            'dealer-filter': carData.dealerFilter,
        };
    }

    specParams() {
        const carData = this.car.data();
        const { specMap } = carData;
        return Object.entries(specMap).reduce((map, [key, val]) => {
            map[`spec-id:${key}`] = val;
            return map;
        }, {});
    }

    financeParams() {
        const result = this.finance.type !== 'cash' && this.finance.result ? this.finance.result : {};
        return {
            'finance-offer-term': result.term?.term,
            'finance-offer-mileage': result.expectedUsage?.value,
            'finance-offer-deposit': result.payments?.deposit?.grossValue,
            'finance-offer-type': this.finance.type,
            'finance-final-payment': null, // TODO: Open query
            'finance-honda-deposit': this.finance.getFinanceContribution(),
            'finance-interest-rate': result.rates?.nominalRate?.percentage,
            'finance-interest-rate-representative': result.rates?.effectiveRate?.percentage,
            'finance-offer-expiry': null, // TODO: Not in solifi response
            'finance-monthly-installment': result.payments?.regularPayment?.grossValue,
        };
    }

    partExParams() {
        return {

        };
    }

    replace(str, data) {
        return str
            .replaceAll('%7B', '{')
            .replaceAll('%7D', '}')
            .replace(/{([^}]+)}/g, (ignore, key) => {
                const val = `${data[key] || ''}`.replace(/'/g, '');
                return val ? encodeURIComponent(val) : `{${key}}`;
            });
    }

    removeLeftoverParams(link) {
        link.href = link.href.replace(/{([^}]+)}/g, (ignore, key) => {
           return '';
       });
    }
}
